/*
 * Marketing, Connect (2026-10-30): one list of everything marketing hooks up to, so the owner never hunts for where to
 * connect something. Each row says if it is done, what it does, and has one button to the place it is set up.
 * Search Console, ad spend and posting link once HQ saves the keys and the company its account ids (2026-10-30). The
 * outreach inbox can't be linked yet and says so plainly instead of hiding.
 * 2026-10-30 owner: no separate Connect tab. Each row shows on the tab it belongs to (Google profile on Google profile,
 * spend on Ads and Where clients come from, and so on), and the menu counts what each tab still needs.
 * 2026-11-01 owner: sign in with Google or Facebook and pick the profile, Page and ad account (MarketingSignin.tsx),
 * instead of typing ids and adding a service account. Typing the ids in Marketing settings still works.
 * 2026-11-01 owner: the Google profile tab was a wall of rows and banners. It now opens with one listing card
 * (ProfileCard), done connections stay quiet, and everything still open is one Needs you list on the Overview. The review
 * link and the posting switches live in Marketing settings, review requests in the automatic messages.
 */
import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { useSettings } from "../api/hooks";
import { useAds, useGbp, useGbpChoices, usePickGbp, useWebRank } from "../api/marketing";
import { useMkDashboard } from "../api/marketing-tabs";
import { useSession } from "../app/session";
import { InputText } from "../components/fields";
import { plural } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { Icon } from "../components/Icon";
import { ago, Button, Card, Skeleton, StatusLabel, type Status } from "../components/parts";
import { SigninButtons } from "./MarketingSignin";

const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");

/**
 * Picks which Google listing is the company's (2026-10-30). The first listing Google finds by name can be another
 * business, so the owner sees each one's address and picks. Searching costs a Places call, so it runs only when open.
 */
function GbpPick({ current, word }: { current: string | null; word: string }) {
  const { company } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [words, setWords] = useState("");
  const [q, setQ] = useState("");
  const choices = useGbpChoices(company.id, q, open);
  const pick = usePickGbp();
  const close = () => setOpen(false);
  return (
    <>
      <Button type={current ? "tertiary" : "primary"} size="small" onClick={() => setOpen(true)}>
        {word}
      </Button>
      <Modal title="Pick your Google listing" open={open} onClose={close}>
        <form
          className="mk-pick-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(words.trim());
          }}
        >
          <InputText
            label="Search Google"
            placeholder={company.name}
            value={words}
            onChange={(e) => setWords(e.target.value)}
            wide
          />
          <Button submit type="secondary" size="small" loading={choices.isFetching}>
            Search
          </Button>
        </form>
        <p className="muted">Add the town if you see the wrong businesses.</p>
        {choices.isError ? (
          <p className="banner error">{errText(choices.error)}</p>
        ) : !choices.data ? (
          <Skeleton rows={3} />
        ) : !choices.data.length ? (
          <p className="muted">Google found nothing with those words. Try your business name and town.</p>
        ) : (
          <ul className="auto-list">
            {choices.data.map((c) => (
              <li key={c.placeId} className="setting-row mk-connect-row">
                <span className="what">
                  <b>
                    {c.name} {c.placeId === current ? <StatusLabel status="success">In use now</StatusLabel> : null}
                  </b>
                  <span>
                    {c.address || "No address on Google"}. {plural(c.reviews, "review")}.
                  </span>
                </span>
                <span className="mk-connect-actions">
                  <a className="btn tertiary small" href={c.mapsUrl} target="_blank" rel="noreferrer">
                    See on Maps
                  </a>
                  {c.placeId === current ? null : (
                    <Button
                      size="small"
                      loading={pick.isPending && pick.variables === c.placeId}
                      disabled={pick.isPending}
                      onClick={() =>
                        pick.mutate(c.placeId, {
                          onSuccess: () => {
                            toast("Listing saved and read");
                            close();
                          },
                          onError: (e) => toast(errText(e), { kind: "error" }),
                        })
                      }
                    >
                      This is mine
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}

type State = "done" | "found" | "todo" | "test" | "later";

const STATE_WORDS: Record<State, [Status, string]> = {
  done: ["success", "Connected"],
  // Read from public Maps with no account signed in: true, but not a link to the owner's Google account.
  found: ["informative", "Found on Google"],
  todo: ["warning", "Needs you"],
  test: ["informative", "Test mode"],
  later: ["inactive", "Coming later"],
};

interface Step {
  key: string;
  title: string;
  line: ReactNode;
  state: State;
  action: ReactNode;
  /** The Marketing tabs this row shows on. */
  tabs: string[];
  /** The tab the action opens, if it opens one; hidden while already on it. */
  goes?: string | undefined;
}

/** Every connection row, and how many still need the owner. */
export function useConnectSteps(onTab: (t: string) => void): { steps: Step[] | null; open: number } {
  const { company, can } = useSession();
  const settingsOk = can("settings.read");
  const edit = can("marketing.update");
  const gbp = useGbp(company.id);
  const web = useWebRank(company.id);
  const ads = useAds(company.id);
  const dash = useMkDashboard(company.id);
  const s = useSettings(company.id).data?.settings;
  if (!gbp.data || !dash.data || (settingsOk && !s)) return { steps: null, open: 0 };

  const open = (to: string, hash?: string) => (
    <Link className="btn secondary small" to={to as "/marketing"} {...(hash ? { hash } : {})}>
      Set up
    </Link>
  );
  const goTab = (t: string, word = "Open") => (
    <Button type="tertiary" size="small" onClick={() => onTab(t)}>
      {word}
    </Button>
  );

  const p = gbp.data.profile;
  const { managedBy, postingConnected } = gbp.data;
  const signedIn = Boolean(managedBy) && postingConnected;
  const steps: Step[] = [
    {
      key: "gbp",
      // Shows only on the Google profile tab, so its buttons stay (no Open to the tab it is on).
      tabs: ["profile"],
      title: "Google business profile",
      line: p ? (
        <>
          <span>
            {p.name}, {p.address || "address shows after the next read"}.
          </span>
          <span>
            {signedIn
              ? `Managed by ${managedBy ?? ""}.`
              : "Read from public Google Maps. Sign in with the Google account that manages it so posts and review replies can go out."}{" "}
            {p.rating === null ? "No rating yet" : `${p.rating.toFixed(1)} stars`}, {plural(p.reviewCount, "review")}.
            {p.syncedAt ? ` Read ${ago(p.syncedAt).toLowerCase()}.` : ""}
          </span>
        </>
      ) : (
        "Find your listing on Google and pick the right one. We read your rating and reviews each night."
      ),
      state: p ? (signedIn ? "done" : "found") : "todo",
      action: p ? (
        <span className="mk-connect-actions">
          {signedIn ? null : <SigninButtons kinds={["google"]} tab="profile" />}
          <a className="btn tertiary small" href={p.mapsUrl} target="_blank" rel="noreferrer">
            See on Maps
          </a>
          {edit ? <GbpPick current={p.placeId} word="Wrong one?" /> : null}
        </span>
      ) : edit ? (
        <span className="mk-connect-actions">
          <SigninButtons kinds={["google"]} tab="profile" />
          <GbpPick current={null} word="Find my listing" />
        </span>
      ) : null,
    },
  ];
  if (s) {
    steps.push(
      {
        key: "review",
        tabs: [],
        title: "Google review link",
        line: s.marketing.reviewUrl
          ? "Saved. Review request texts send people here."
          : "The link clients tap to leave a review. Review requests stay off until it is saved.",
        state: s.marketing.reviewUrl ? "done" : "todo",
        action: open("/marketing/settings"),
      },
      {
        key: "asks",
        tabs: [],
        title: "Review requests",
        line: dash.data.reviewsOn
          ? `On. ${plural(dash.data.reviewsAsked30, "client")} asked in the past 30 days.`
          : "One text after a client pays. This brings in most new reviews.",
        state: dash.data.reviewsOn ? "done" : "todo",
        action: open("/settings/automations"),
      },
      {
        key: "site",
        tabs: ["ranks"],
        title: "Website",
        line: s.website.origins.length
          ? `${s.website.origins.join(", ")} sends requests here.`
          : "Add your website so its quote form sends requests here and its traffic can be read.",
        state: s.website.origins.length ? "done" : "todo",
        action: open("/requests/settings"),
      },
      {
        key: "send",
        tabs: ["campaigns", "due"],
        title: "Real texts and emails",
        line: s.messaging.live
          ? "On. Campaigns, review requests and due again texts go to clients."
          : "Off for now, so nothing reaches clients. Campaigns and review requests are kept as tests.",
        state: s.messaging.live ? "done" : "test",
        action: open("/settings/messages", "sending"),
      },
    );
  }
  steps.push(
    {
      key: "console",
      tabs: ["ranks"],
      goes: web.data?.connected ? "ranks" : undefined,
      title: "Google Search Console",
      line: web.data?.connected
        ? "Linked. Website searches show on Website rank, read each week."
        : "Shows which Google searches bring people to your website. Sign in with a Google account that is a user in Search Console.",
      state: web.data?.connected ? "done" : "todo",
      action: web.data?.connected ? goTab("ranks", "Website rank") : <SigninButtons kinds={["google"]} tab="ranks" />,
    },
    {
      key: "spend",
      tabs: ["ads", "channels"],
      goes: settingsOk && !ads.data?.spendConnected ? undefined : "channels",
      title: "Google Ads and Meta ad spend",
      line: ads.data?.spendConnected
        ? "Linked. Spend comes in each night and replaces what was typed on Channels."
        : "Sign in with Google or Facebook and pick your ad account. Until then, type what you spend on Channels to see cost per new client.",
      state: ads.data?.spendConnected ? "done" : "todo",
      action: ads.data?.spendConnected ? (
        goTab("channels", "Channels")
      ) : can("settings.update") ? (
        <SigninButtons kinds={["google", "meta"]} tab="ads" />
      ) : (
        goTab("channels", "Type spend")
      ),
    },
    {
      key: "posting",
      tabs: ["social"],
      goes: gbp.data.postingConnected || !settingsOk ? "social" : undefined,
      title: "Posting to Google, Facebook and Instagram",
      line: gbp.data.postingConnected
        ? s?.marketing.postingLive
          ? "Linked and on. Approved posts and review replies go out."
          : "Linked. Turn on posting in Marketing settings when you are ready. Until then posts wait as held."
        : "Sign in with Google or Facebook and pick your profile or Page. Until then, approved posts and replies wait as held.",
      state: gbp.data.postingConnected ? "done" : "todo",
      action: gbp.data.postingConnected ? (
        goTab("social", "Posts")
      ) : can("settings.update") ? (
        <SigninButtons kinds={["google", "meta"]} tab="social" />
      ) : (
        goTab("social", "Posts")
      ),
    },
    {
      key: "outreach",
      tabs: ["email"],
      goes: "email",
      title: "Cold email inbox",
      line: "Needs its own outreach domain, never a brand's main email. Emails are kept as tests until then.",
      state: "later",
      action: goTab("email", "Cold email"),
    },
  );
  return { steps, open: steps.filter((x) => x.state === "todo").length };
}

/** How many rows still need the owner on one tab (the side menu shows it). The Overview counts all of them. */
export const openOn = (steps: Step[] | null, tab: string) =>
  steps ? steps.filter((x) => x.state === "todo" && (tab === "dash" || x.tabs.includes(tab))).length : 0;

function Row({ step, tab }: { step: Step; tab: string }) {
  const [status, word] = STATE_WORDS[step.state];
  return (
    <li className="setting-row mk-connect-row">
      <span className="what">
        <b>
          {step.title} <StatusLabel status={status}>{word}</StatusLabel>
        </b>
        <span>{step.line}</span>
      </span>
      {step.goes === tab ? null : step.action}
    </li>
  );
}

/**
 * What is still open on one tab, shown at its top. Done connections say nothing here (Marketing settings lists the linked
 * accounts). The Google profile tab has its own listing card instead.
 */
export function TabConnections({ tab, onTab }: { tab: string; onTab: (t: string) => void }) {
  const { steps } = useConnectSteps(onTab);
  if (tab === "profile") return null;
  const mine = steps?.filter((x) => x.tabs.includes(tab) && x.state !== "done") ?? [];
  if (!mine.length) return null;
  return (
    <Card title="To set up" flush>
      <ul className="auto-list">
        {mine.map((x) => (
          <Row key={x.key} step={x} tab={tab} />
        ))}
      </ul>
    </Card>
  );
}

/**
 * The Overview's Needs you list (2026-11-01): every connection still open, reviews waiting for a reply, and posting left
 * off. Nothing shows once all is done.
 */
export function NeedsYou({ onTab }: { onTab: (t: string) => void }) {
  const { company, can } = useSession();
  const { steps } = useConnectSteps(onTab);
  const gbp = useGbp(company.id).data;
  const m = useSettings(company.id).data?.settings.marketing;
  if (!steps || !gbp) return null;
  const items: { key: string; title: string; line: ReactNode; action: ReactNode }[] = steps
    .filter((x) => x.state === "todo")
    .map((x) => ({ key: x.key, title: x.title, line: x.line, action: x.action }));
  if (gbp.waitingReplies)
    items.push({
      key: "replies",
      title: `${plural(gbp.waitingReplies, "review")} waiting for a reply`,
      line: "Answer them on the Google profile tab. A quick reply shows people you care.",
      action: (
        <Button type="secondary" size="small" onClick={() => onTab("profile")}>
          Reply
        </Button>
      ),
    });
  if (gbp.postingConnected && m && !m.postingLive && can("settings.read"))
    items.push({
      key: "posting-off",
      title: "Posting is off",
      line: "Approved posts and review replies wait as held until it is on.",
      action: (
        <Link className="btn secondary small" to="/marketing/settings" hash="posting">
          Turn on
        </Link>
      ),
    });
  if (!items.length) return null;
  return (
    <Card title="Needs you" action={<span className="muted num">{plural(items.length, "thing")}</span>} flush>
      <ul className="auto-list">
        {items.map((x) => (
          <li key={x.key} className="setting-row mk-row mk-connect-row">
            <span className="mk-row-icon mk-need-icon" aria-hidden>
              <Icon name="warning" size={18} />
            </span>
            <span className="what">
              <b>{x.title}</b>
              <span>{x.line}</span>
            </span>
            {x.action}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * The top of the Google profile tab (2026-11-01): which listing it is, whether the owner's Google account is signed in,
 * and what to do with it. Rating and reviews are on the tiles under it.
 */
export function ProfileCard() {
  const { company, can } = useSession();
  const q = useGbp(company.id);
  const edit = can("marketing.update");
  if (!q.data) return <Skeleton rows={2} />;
  const p = q.data.profile;
  const { managedBy, postingConnected } = q.data;
  const signedIn = Boolean(managedBy) && postingConnected;
  if (!p)
    return (
      <Card title="Your Google listing">
        <div className="mk-listing">
          <p className="muted">
            Find your business on Google and pick the right one. We read your rating and reviews each night.
          </p>
          {edit ? (
            <span className="mk-connect-actions">
              <SigninButtons kinds={["google"]} tab="profile" />
              <GbpPick current={null} word="Find my listing" />
            </span>
          ) : null}
        </div>
      </Card>
    );
  return (
    <Card
      title={p.name}
      action={
        <span className="mk-connect-actions">
          <a className="btn tertiary small" href={p.mapsUrl} target="_blank" rel="noreferrer">
            See on Maps
          </a>
          {edit ? <GbpPick current={p.placeId} word="Wrong one?" /> : null}
        </span>
      }
    >
      <div className="mk-listing">
        <div className="stack tight">
          <span>{p.address || "The address shows after the next read."}</span>
          <span className="muted small">
            {signedIn
              ? `Signed in as ${managedBy ?? ""}. Replies and posts can go out.`
              : "Read from public Google Maps. Sign in with the Google account that manages it so replies and posts can go out."}
          </span>
        </div>
        {signedIn ? <StatusLabel status="success">Signed in</StatusLabel> : <SigninButtons kinds={["google"]} tab="profile" />}
      </div>
    </Card>
  );
}
